<script>
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/conversations';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

export default {
  components: {
    NextButton,
    Spinner,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['cancel', 'update:show'],
  data() {
    return {
      instructions: '',
      errorMessage: '',
      isGenerating: false,
      isSending: false,
    };
  },
  computed: {
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    canSend() {
      return !!this.instructions.trim() && !this.isGenerating && !this.isSending;
    },
  },
  watch: {
    show(value) {
      if (value) this.generateSummary();
    },
  },
  methods: {
    onCancel() {
      this.$emit('cancel');
    },
    async generateSummary() {
      if (!this.currentChat?.id) return;

      this.errorMessage = '';
      this.instructions = '';
      this.isGenerating = true;

      try {
        const { data } = await ConversationApi.generateAiAgentSummary(
          this.currentChat.id
        );
        this.instructions = data.summary || '';
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          'Nao foi possivel gerar as instrucoes do agente de IA.';
      } finally {
        this.isGenerating = false;
      }
    },
    async sendToAgent() {
      if (!this.canSend) return;

      this.errorMessage = '';
      this.isSending = true;

      try {
        await ConversationApi.dispatchAiAgent(
          this.currentChat.id,
          this.instructions.trim()
        );
        useAlert('Instrucoes enviadas para o agente de IA.');
        this.onCancel();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          'Nao foi possivel enviar as instrucoes para o agente de IA.';
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onCancel" size="medium">
    <div class="flex flex-col max-h-[82vh] overflow-hidden">
      <woot-modal-header
        header-title="Chamar agente de IA"
        header-content="Revise as instrucoes geradas antes de enviar para o agente."
      />
      <div class="flex flex-col gap-4 px-8 pb-8">
        <div
          v-if="isGenerating"
          class="flex items-center gap-3 min-h-[16rem] text-sm text-n-slate-11"
        >
          <Spinner class="size-4" />
          <span>Gerando resumo completo da conversa...</span>
        </div>
        <textarea
          v-else
          v-model="instructions"
          class="w-full min-h-[22rem] resize-y rounded-lg border border-n-strong bg-n-alpha-2 p-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          placeholder="As instrucoes geradas pela IA vao aparecer aqui."
        />
        <p v-if="errorMessage" class="text-sm text-n-ruby-9">
          {{ errorMessage }}
        </p>
        <div class="flex justify-end gap-2">
          <NextButton
            faded
            slate
            label="Cancelar"
            :disabled="isSending"
            @click.prevent="onCancel"
          />
          <NextButton
            outline
            slate
            label="Gerar novamente"
            :disabled="isGenerating || isSending"
            :is-loading="isGenerating"
            @click.prevent="generateSummary"
          />
          <NextButton
            label="Enviar para agente"
            :disabled="!canSend"
            :is-loading="isSending"
            @click.prevent="sendToAgent"
          />
        </div>
      </div>
    </div>
  </woot-modal>
</template>
